import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { StatusBadge } from "../StatusBadge";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";

/** Left pane of the 2-pane Payroll Line Detail workspace (Payroll & Leave Deep Audit item 6).
 * Lets a preparer/reviewer page through every employee in the period without returning to the
 * Period Detail page - supports search, status filter, and Up/Down keyboard navigation (the
 * keydown listener lives on the page so it works regardless of which pane has focus). */
export function PayrollLineListRail({ lines, selectedId, onSelect, testId }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const statuses = useMemo(() => Array.from(new Set(lines.map((l) => l.status))), [lines]);
  const filtered = useMemo(() => lines.filter((l) => {
    if (statusFilter !== "ALL" && l.status !== statusFilter) return false;
    if (search && !l.employee_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [lines, search, statusFilter]);

  return (
    <div className="flex flex-col h-full" data-testid={testId}>
      <div className="p-3 border-b border-gray-200 space-y-2 shrink-0">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee..." className="pl-8 h-8 text-xs" data-testid={`${testId}-search`} />
        </div>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setStatusFilter("ALL")} data-testid={`${testId}-filter-all`}
            className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full border", statusFilter === "ALL" ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 text-gray-500 hover:bg-gray-50")}>
            All ({lines.length})
          </button>
          {statuses.map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`${testId}-filter-${s}`}
              className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full border", statusFilter === s ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 text-gray-500 hover:bg-gray-50")}>
              {s.replaceAll("_", " ")}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.map((l) => {
          const hasWarning = (l.shift_premium_review_flags || []).length > 0 || (l.adjustments || []).some((a) => a.status === "PENDING");
          return (
            <button
              key={l.id} onClick={() => onSelect(l.id)} data-testid={`${testId}-item-${l.id}`}
              className={cn("w-full text-left px-3 py-2.5 border-b border-gray-100 transition-colors", selectedId === l.id ? "bg-blue-50 border-l-2 border-l-blue-600" : "hover:bg-gray-50 border-l-2 border-l-transparent")}
            >
              <div className="flex items-center justify-between gap-1">
                <p className="text-sm font-semibold text-gray-800 truncate">{l.employee_name}</p>
                {hasWarning && <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" data-testid={`${testId}-warning-${l.id}`} title="Needs review" />}
              </div>
              <p className="text-[11px] text-gray-500">{l.store_location || "-"}</p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs font-semibold text-gray-700">¥{Number(l.gross_pay || 0).toLocaleString()}</span>
                <StatusBadge status={l.status} />
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && <p className="text-xs text-gray-500 text-center py-6" data-testid={`${testId}-empty`}>No matching employees</p>}
      </div>
    </div>
  );
}
